import type { ComponentProps, ReactNode } from 'react';
import { RecordTable as ConsoleRecordTable, type Record_ } from '@ui/console/RecordTable';
import {
  CopyableId,
  EntityRef,
  looksLikeUuid,
  refColumnLabel,
  refForField,
  REF_NOUN,
  type RefKind,
} from './references';

export type { Record_ };

const ID_SUFFIX = /(^id$|_id$|_uuid$|_ref$|_reference$)/;

/**
 * The console's generic table, with admin's references switched on.
 *
 * Any column that names a member, staff account, stylist, garment, outfit,
 * import or workflow job by id renders as that thing's name, linked to its
 * page, with the id secondary; any other uuid-valued id column renders as a
 * short copyable id instead of thirty-six characters of noise. The row
 * drawer gets the same treatment.
 */
export function RecordTable({
  memberId,
  rowKind,
  renderCell,
  columnLabel,
  ...props
}: ComponentProps<typeof ConsoleRecordTable> & {
  /** The member every row belongs to, when the rows do not say. */
  memberId?: string | undefined;
  /** What the row's own `id` is, so it can render as a named link too. */
  rowKind?: RefKind;
}) {
  const render = (column: string, record: Record_): ReactNode | undefined => {
    const own = renderCell?.(column, record);
    if (own !== undefined) return own;
    const value = record[column];
    const context =
      memberId && record['member_id'] === undefined ? { ...record, member_id: memberId } : record;
    if (column === 'id' && rowKind && typeof value === 'string' && value) {
      const label = record['name'] ?? record['title'] ?? record['display_title'];
      return (
        <EntityRef
          kind={rowKind}
          id={value}
          {...(typeof label === 'string' && label ? { label } : {})}
          {...(memberId ? { memberId } : {})}
        />
      );
    }
    const ref = refForField(column, value, context);
    if (ref) return <EntityRef {...ref} />;
    if (ID_SUFFIX.test(column) && looksLikeUuid(value))
      return <CopyableId value={value} label={column.replace(/_/g, ' ')} />;
    return undefined;
  };
  const label = (column: string) =>
    columnLabel?.(column) ??
    (column === 'id' ? (rowKind ? REF_NOUN[rowKind] : 'Reference') : refColumnLabel(column));
  return <ConsoleRecordTable {...props} renderCell={render} columnLabel={label} />;
}
